import { getLocalStorage, setLocalStorage } from './localStorage';

export type CarerStatus = 'available' | 'in-progress' | 'completed' | 'offline';
export type GeofenceStatus = 'inside' | 'outside' | 'fallback';
export type VisitScheduleStatus = 'on-time' | 'late' | 'early' | 'unscheduled' | 'completed';

export interface VisitAuditEvent {
  id: string;
  type:
    | 'check-in'
    | 'check-out'
    | 'gps-check'
    | 'task-evidence'
    | 'payroll-lock'
    | 'exception';
  label: string;
  createdAt: string;
  locationSource?: 'device-gps' | 'scheduled-address';
  latitude?: number;
  longitude?: number;
  distanceFromClientMeters?: number;
}

export interface VisitTaskEvidence {
  id: string;
  task: string;
  status: 'completed' | 'not-completed';
  note?: string;
  recordedAt: string;
  caregiverId: string;
  caregiverName: string;
}

export interface VisitVerificationRecord {
  visitId: string;
  caregiverId: string;
  caregiverName: string;
  clientName: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  scheduledDurationMinutes: number;
  actualCheckInAt?: string;
  actualCheckOutAt?: string;
  realDurationMinutes?: number;
  varianceMinutes?: number;
  distanceFromClientMeters?: number;
  gpsAccuracyMeters?: number;
  geofenceStatus: GeofenceStatus;
  scheduleStatus: VisitScheduleStatus;
  payrollLocked: boolean;
  hoursBlocked: boolean;
  exceptions: string[];
  taskEvidence: VisitTaskEvidence[];
  auditTrail: VisitAuditEvent[];
  updatedAt: string;
}

export interface LiveCarerTrackingRecord {
  caregiverId: string;
  caregiverName: string;
  email: string;
  status: CarerStatus;
  currentClient?: string;
  currentVisit?: string;
  currentTask?: string;
  address?: string;
  latitude: number;
  longitude: number;
  locationSource: 'device-gps' | 'scheduled-address';
  scheduleMatched: boolean;
  payrollLocked: boolean;
  lastUpdated: string;
  activeVisitId?: string;
  scheduledStartAt?: string;
  scheduledEndAt?: string;
  actualCheckInAt?: string;
  actualCheckOutAt?: string;
  scheduledDurationMinutes?: number;
  realDurationMinutes?: number;
  varianceMinutes?: number;
  distanceFromClientMeters?: number;
  gpsAccuracyMeters?: number;
  geofenceStatus?: GeofenceStatus;
  scheduleStatus?: VisitScheduleStatus;
  exceptions?: string[];
  taskEvidence?: VisitTaskEvidence[];
  auditTrail?: VisitAuditEvent[];
}

export const LIVE_CARER_TRACKING_KEY = 'admin:live-carer-tracking';
export const VISIT_VERIFICATION_KEY = 'admin:visit-verification-records';

type LiveTelemetryResult = {
  ok: boolean;
  source: 'api' | 'local';
  data: LiveCarerTrackingRecord[];
};

type ApiResponse = {
  data?: LiveCarerTrackingRecord[];
};

const REQUEST_TIMEOUT_MS = 3_500;

function withTimeout(url: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  return fetch(url, {
    ...init,
    signal: controller.signal,
  }).finally(() => window.clearTimeout(timer));
}

function recordsFromApiResponse(response: ApiResponse): LiveCarerTrackingRecord[] {
  return Array.isArray(response.data) ? response.data : [];
}

export function getStoredLiveCarerRecords(
  defaultRecords: LiveCarerTrackingRecord[] = []
): LiveCarerTrackingRecord[] {
  return getLocalStorage<LiveCarerTrackingRecord[]>(LIVE_CARER_TRACKING_KEY, defaultRecords);
}

export function upsertStoredLiveCarerRecord(record: LiveCarerTrackingRecord): LiveCarerTrackingRecord[] {
  const currentRecords = getStoredLiveCarerRecords();
  const nextRecords = [
    record,
    ...currentRecords.filter((current) => current.caregiverId !== record.caregiverId),
  ].slice(0, 50);

  setLocalStorage(LIVE_CARER_TRACKING_KEY, nextRecords);
  return nextRecords;
}

export function getStoredVisitVerificationRecords(
  defaultRecords: VisitVerificationRecord[] = []
): VisitVerificationRecord[] {
  return getLocalStorage<VisitVerificationRecord[]>(VISIT_VERIFICATION_KEY, defaultRecords);
}

export function upsertStoredVisitVerificationRecord(record: VisitVerificationRecord): VisitVerificationRecord[] {
  const currentRecords = getStoredVisitVerificationRecords();
  const nextRecords = [
    record,
    ...currentRecords.filter((current) => current.visitId !== record.visitId),
  ].slice(0, 250);

  setLocalStorage(VISIT_VERIFICATION_KEY, nextRecords);
  return nextRecords;
}

export async function publishLiveCarerStatus(record: LiveCarerTrackingRecord): Promise<LiveTelemetryResult> {
  upsertStoredLiveCarerRecord(record);

  try {
    const response = await withTimeout('/api/live-carers', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error(`Telemetry API returned ${response.status}`);
    }

    const nextRecords = recordsFromApiResponse((await response.json()) as ApiResponse);
    if (nextRecords.length > 0) {
      setLocalStorage(LIVE_CARER_TRACKING_KEY, nextRecords);
    }

    return {
      ok: true,
      source: 'api',
      data: nextRecords.length > 0 ? nextRecords : getStoredLiveCarerRecords(),
    };
  } catch {
    return {
      ok: false,
      source: 'local',
      data: getStoredLiveCarerRecords(),
    };
  }
}

export async function fetchLiveCarerRecords(
  defaultRecords: LiveCarerTrackingRecord[] = []
): Promise<LiveTelemetryResult> {
  try {
    const response = await withTimeout('/api/live-carers');

    if (!response.ok) {
      throw new Error(`Telemetry API returned ${response.status}`);
    }

    const records = recordsFromApiResponse((await response.json()) as ApiResponse);
    const nextRecords = records.length > 0 ? records : defaultRecords;
    setLocalStorage(LIVE_CARER_TRACKING_KEY, nextRecords);

    return {
      ok: true,
      source: 'api',
      data: nextRecords,
    };
  } catch {
    return {
      ok: false,
      source: 'local',
      data: getStoredLiveCarerRecords(defaultRecords),
    };
  }
}
